<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import PortalListingSyncsAPI from 'dashboard/api/portalListingSyncs';
import { useAlert } from 'dashboard/composables';
import { usePolicy } from 'dashboard/composables/usePolicy';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import LaisStatusBadge from 'dashboard/components-next/LaisStatusBadge.vue';
import { LAIS_IMOVEIS_PERMISSIONS } from 'dashboard/routes/dashboard/laisRouteHelpers';
import {
  PORTAL_OPERATIONAL_STATES,
  PORTAL_SYNC_STATUSES,
  attemptCountEntries,
  canOperateSync,
  disabledReasonForSync,
  normalizeSync,
  normalizeSyncsResponse,
  operationPayload,
  portalSyncTone,
} from '../utils/portalSyncOperations';

const { t } = useI18n();
const { checkPermissions } = usePolicy();

const syncs = ref([]);
const meta = ref({
  count: 0,
  total_count: 0,
  page: 1,
  per_page: 50,
  total_pages: 0,
});
const summaryPayload = ref({});
const selectedSync = ref(null);
const portalFilter = ref('');
const statusFilter = ref('');
const operationalFilter = ref('');
const dueOnly = ref(false);
const searchQuery = ref('');
const operationReason = ref('');
const isLoading = ref(false);
const isLoadingSync = ref(false);
const actionKey = ref('');
const errorMessage = ref('');

const statusLabel = status => {
  switch (status) {
    case 'pending':
      return t('PROPERTIES.PORTAL_SYNC_OPERATIONS.STATUSES.PENDING');
    case 'queued':
      return t('PROPERTIES.PORTAL_SYNC_OPERATIONS.STATUSES.QUEUED');
    case 'submitted':
      return t('PROPERTIES.PORTAL_SYNC_OPERATIONS.STATUSES.SUBMITTED');
    case 'accepted':
      return t('PROPERTIES.PORTAL_SYNC_OPERATIONS.STATUSES.ACCEPTED');
    case 'processing':
      return t('PROPERTIES.PORTAL_SYNC_OPERATIONS.STATUSES.PROCESSING');
    case 'published':
      return t('PROPERTIES.PORTAL_SYNC_OPERATIONS.STATUSES.PUBLISHED');
    case 'rejected':
      return t('PROPERTIES.PORTAL_SYNC_OPERATIONS.STATUSES.REJECTED');
    case 'unpublish_pending':
      return t('PROPERTIES.PORTAL_SYNC_OPERATIONS.STATUSES.UNPUBLISH_PENDING');
    case 'unpublished':
      return t('PROPERTIES.PORTAL_SYNC_OPERATIONS.STATUSES.UNPUBLISHED');
    case 'failed':
      return t('PROPERTIES.PORTAL_SYNC_OPERATIONS.STATUSES.FAILED');
    default:
      return t('PROPERTIES.PORTAL_SYNC_OPERATIONS.STATUSES.UNKNOWN');
  }
};

const operationalLabel = status => {
  switch (status) {
    case 'published':
      return t(
        'PROPERTIES.PORTAL_SYNC_OPERATIONS.OPERATIONAL_STATES.PUBLISHED'
      );
    case 'rejected':
      return t('PROPERTIES.PORTAL_SYNC_OPERATIONS.OPERATIONAL_STATES.REJECTED');
    case 'unknown':
      return t('PROPERTIES.PORTAL_SYNC_OPERATIONS.OPERATIONAL_STATES.UNKNOWN');
    case 'failed':
      return t('PROPERTIES.PORTAL_SYNC_OPERATIONS.OPERATIONAL_STATES.FAILED');
    case 'unpublished':
      return t(
        'PROPERTIES.PORTAL_SYNC_OPERATIONS.OPERATIONAL_STATES.UNPUBLISHED'
      );
    default:
      return t(
        'PROPERTIES.PORTAL_SYNC_OPERATIONS.OPERATIONAL_STATES.PROCESSING'
      );
  }
};

const readinessTone = status => {
  if (['ready', 'published'].includes(status)) return 'positive';
  if (
    [
      'credential_invalid',
      'payload_invalid',
      'provider_unavailable',
      'divergent',
      'attention_required',
    ].includes(status)
  ) {
    return 'danger';
  }
  if (
    ['pending_reconciliation', 'reconciliation_due', 'syncing'].includes(status)
  ) {
    return 'warning';
  }

  return 'neutral';
};

const failureLabel = failure => {
  if (!failure || failure.category === 'none') {
    return t('PROPERTIES.PORTAL_SYNC_OPERATIONS.READINESS.NO_FAILURE');
  }

  return t('PROPERTIES.PORTAL_SYNC_OPERATIONS.READINESS.FAILURE', {
    category: failure.category,
    code: failure.code || 'n/a',
  });
};

const portalOptions = computed(() => [
  { value: '', label: t('PROPERTIES.PORTAL_SYNC_OPERATIONS.ALL_PORTALS') },
  { value: 'olx', label: 'OLX' },
  { value: 'vrsync', label: 'VRSync' },
]);

const statusOptions = computed(() =>
  PORTAL_SYNC_STATUSES.map(value => ({
    value,
    label: value
      ? statusLabel(value)
      : t('PROPERTIES.PORTAL_SYNC_OPERATIONS.ALL_STATUS'),
  }))
);

const operationalOptions = computed(() =>
  PORTAL_OPERATIONAL_STATES.map(value => ({
    value,
    label: value
      ? operationalLabel(value)
      : t('PROPERTIES.PORTAL_SYNC_OPERATIONS.ALL_OPERATIONAL_STATES'),
  }))
);

const summary = computed(() => {
  const operationalCounts =
    summaryPayload.value.operational_state_counts ||
    summaryPayload.value.operationalStateCounts ||
    {};

  return {
    total: meta.value.total_count || syncs.value.length,
    processing: Number(operationalCounts.processing || 0),
    rejected: Number(operationalCounts.rejected || 0),
    published: Number(operationalCounts.published || 0),
    retryable: Number(summaryPayload.value.retryable_count || 0),
  };
});

const hasSyncs = computed(() => syncs.value.length > 0);
const hasPreviousPage = computed(() => Number(meta.value.page || 1) > 1);
const hasNextPage = computed(
  () => Number(meta.value.page || 1) < Number(meta.value.total_pages || 0)
);
const canOperatePortals = computed(() =>
  checkPermissions(LAIS_IMOVEIS_PERMISSIONS)
);

const queryParams = () => ({
  page: meta.value.page || 1,
  per_page: meta.value.per_page || 50,
  portal: portalFilter.value || undefined,
  status: statusFilter.value || undefined,
  operational_state: operationalFilter.value || undefined,
  due: dueOnly.value || undefined,
  q: searchQuery.value || undefined,
});

const formatDateTime = value => {
  if (!value) return t('PROPERTIES.PORTAL_SYNC_OPERATIONS.NO_DATE');

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return t('PROPERTIES.PORTAL_SYNC_OPERATIONS.NO_DATE');
  }

  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const fetchSyncs = async () => {
  isLoading.value = true;
  errorMessage.value = '';
  try {
    const { data } = await PortalListingSyncsAPI.list(queryParams());
    const normalized = normalizeSyncsResponse(data);
    syncs.value = normalized.syncs;
    meta.value = normalized.meta;
    summaryPayload.value = normalized.summary;
  } catch {
    errorMessage.value = t('PROPERTIES.PORTAL_SYNC_OPERATIONS.ERRORS.LOAD');
  } finally {
    isLoading.value = false;
  }
};

const applyFilters = () => {
  meta.value = { ...meta.value, page: 1 };
  fetchSyncs();
};

const changePage = direction => {
  const nextPage = Number(meta.value.page || 1) + direction;
  if (nextPage < 1 || nextPage > Number(meta.value.total_pages || 0)) return;

  meta.value = { ...meta.value, page: nextPage };
  fetchSyncs();
};

const replaceSync = sync => {
  const index = syncs.value.findIndex(item => item.id === sync.id);
  if (index === -1) return;

  syncs.value.splice(index, 1, sync);
};

const loadSync = async sync => {
  if (!sync?.id) return;

  isLoadingSync.value = true;
  try {
    const { data } = await PortalListingSyncsAPI.show(sync.id);
    selectedSync.value = normalizeSync(data?.payload);
    replaceSync(selectedSync.value);
  } catch {
    useAlert(t('PROPERTIES.PORTAL_SYNC_OPERATIONS.ERRORS.SYNC'));
  } finally {
    isLoadingSync.value = false;
  }
};

async function performSyncAction(sync, action, request, successMessage) {
  if (!canOperatePortals.value || !sync?.id) return;

  actionKey.value = `${action}:${sync.id}`;
  try {
    const { data } = await request(
      operationPayload({
        action,
        id: sync.id,
        reason:
          operationReason.value ||
          t('PROPERTIES.PORTAL_SYNC_OPERATIONS.DEFAULT_REASON'),
      })
    );
    const updated = normalizeSync(data?.payload?.sync || data?.payload);
    selectedSync.value = updated;
    replaceSync(updated);
    useAlert(successMessage);
    await fetchSyncs();
  } catch (error) {
    useAlert(
      error?.response?.data?.errors?.[0] ||
        t('PROPERTIES.PORTAL_SYNC_OPERATIONS.TOASTS.ERROR')
    );
  } finally {
    actionKey.value = '';
  }
}

const pollSync = sync =>
  performSyncAction(
    sync,
    'poll',
    payload => PortalListingSyncsAPI.poll(sync.id, payload),
    t('PROPERTIES.PORTAL_SYNC_OPERATIONS.TOASTS.POLL')
  );

const retrySync = sync =>
  performSyncAction(
    sync,
    'retry',
    payload => PortalListingSyncsAPI.retry(sync.id, payload),
    t('PROPERTIES.PORTAL_SYNC_OPERATIONS.TOASTS.RETRY')
  );

const reconcileSync = sync =>
  performSyncAction(
    sync,
    'reconcile',
    payload => PortalListingSyncsAPI.reconcile(sync.id, payload),
    t('PROPERTIES.PORTAL_SYNC_OPERATIONS.TOASTS.RECONCILE')
  );

const isActionLoading = (action, id) => actionKey.value === `${action}:${id}`;

onMounted(fetchSyncs);
</script>

<template>
  <main class="flex h-full min-h-0 flex-col overflow-auto bg-n-background">
    <LaisHeader
      :title="t('PROPERTIES.PORTAL_SYNC_OPERATIONS.TITLE')"
      :subtitle="t('PROPERTIES.PORTAL_SYNC_OPERATIONS.SUBTITLE')"
      title-badge="P07"
    />

    <section class="border-b border-n-weak px-4 py-4 sm:px-6">
      <div
        class="grid gap-3 md:grid-cols-2 xl:grid-cols-[0.8fr_0.9fr_1fr_1.1fr_auto]"
      >
        <label class="flex min-w-0 flex-col text-xs text-n-slate-11">
          {{ t('PROPERTIES.PORTAL_SYNC_OPERATIONS.FILTERS.PORTAL') }}
          <select
            v-model="portalFilter"
            class="mt-1 w-full min-w-0 rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm"
          >
            <option
              v-for="option in portalOptions"
              :key="option.value || 'all-portals'"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </label>
        <label class="flex min-w-0 flex-col text-xs text-n-slate-11">
          {{ t('PROPERTIES.PORTAL_SYNC_OPERATIONS.FILTERS.STATUS') }}
          <select
            v-model="statusFilter"
            class="mt-1 w-full min-w-0 rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm"
          >
            <option
              v-for="option in statusOptions"
              :key="option.value || 'all-status'"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </label>
        <label class="flex min-w-0 flex-col text-xs text-n-slate-11">
          {{ t('PROPERTIES.PORTAL_SYNC_OPERATIONS.FILTERS.OPERATIONAL') }}
          <select
            v-model="operationalFilter"
            class="mt-1 w-full min-w-0 rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm"
          >
            <option
              v-for="option in operationalOptions"
              :key="option.value || 'all-operational'"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </label>
        <label class="flex min-w-0 flex-col text-xs text-n-slate-11">
          {{ t('PROPERTIES.PORTAL_SYNC_OPERATIONS.FILTERS.SEARCH') }}
          <input
            v-model="searchQuery"
            type="search"
            class="mt-1 w-full min-w-0 rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm"
            @keyup.enter="applyFilters"
          />
        </label>
        <div
          class="flex min-w-0 flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-end xl:justify-end"
        >
          <label class="flex items-center gap-2 text-xs text-n-slate-11">
            <input v-model="dueOnly" type="checkbox" class="size-4" />
            {{ t('PROPERTIES.PORTAL_SYNC_OPERATIONS.FILTERS.DUE_ONLY') }}
          </label>
          <LaisPrimaryButton
            class="w-full sm:w-auto"
            icon="i-lucide-filter"
            size="sm"
            @click="applyFilters"
          >
            {{ t('PROPERTIES.PORTAL_SYNC_OPERATIONS.FILTERS.APPLY') }}
          </LaisPrimaryButton>
          <LaisPrimaryButton
            class="w-full sm:w-auto"
            variant="ghost"
            icon="i-lucide-refresh-cw"
            size="sm"
            :is-loading="isLoading"
            @click="fetchSyncs"
          >
            {{ t('PROPERTIES.PORTAL_SYNC_OPERATIONS.REFRESH') }}
          </LaisPrimaryButton>
        </div>
      </div>
    </section>

    <section class="grid gap-3 px-4 py-4 sm:px-6 md:grid-cols-2 xl:grid-cols-5">
      <LaisCard class="p-4">
        <p class="text-xs text-n-slate-11">
          {{ t('PROPERTIES.PORTAL_SYNC_OPERATIONS.SUMMARY.TOTAL') }}
        </p>
        <p class="mt-1 text-xl font-semibold text-n-slate-12">
          {{ summary.total }}
        </p>
      </LaisCard>
      <LaisCard class="p-4">
        <p class="text-xs text-n-slate-11">
          {{ t('PROPERTIES.PORTAL_SYNC_OPERATIONS.SUMMARY.PROCESSING') }}
        </p>
        <p class="mt-1 text-xl font-semibold text-lais-primary">
          {{ summary.processing }}
        </p>
      </LaisCard>
      <LaisCard class="p-4">
        <p class="text-xs text-n-slate-11">
          {{ t('PROPERTIES.PORTAL_SYNC_OPERATIONS.SUMMARY.REJECTED') }}
        </p>
        <p class="mt-1 text-xl font-semibold text-n-ruby-11">
          {{ summary.rejected }}
        </p>
      </LaisCard>
      <LaisCard class="p-4">
        <p class="text-xs text-n-slate-11">
          {{ t('PROPERTIES.PORTAL_SYNC_OPERATIONS.SUMMARY.PUBLISHED') }}
        </p>
        <p class="mt-1 text-xl font-semibold text-n-teal-11">
          {{ summary.published }}
        </p>
      </LaisCard>
      <LaisCard class="p-4">
        <p class="text-xs text-n-slate-11">
          {{ t('PROPERTIES.PORTAL_SYNC_OPERATIONS.SUMMARY.RETRYABLE') }}
        </p>
        <p class="mt-1 text-xl font-semibold text-n-amber-11">
          {{ summary.retryable }}
        </p>
      </LaisCard>
    </section>

    <section
      v-if="!canOperatePortals"
      class="mx-4 mb-4 rounded-lg border border-n-amber-7 bg-n-amber-3 px-4 py-3 text-sm text-n-amber-11 sm:mx-6"
      role="status"
      aria-live="polite"
      data-test="portal-permission-state"
    >
      {{ t('PROPERTIES.PORTAL_SYNC_OPERATIONS.PERMISSION') }}
    </section>

    <section
      class="grid min-h-0 gap-4 px-4 pb-6 sm:px-6 xl:grid-cols-[1.45fr_0.9fr]"
    >
      <div class="min-w-0">
        <p
          v-if="errorMessage"
          class="mb-3 text-sm text-n-ruby-11"
          role="alert"
          data-test="portal-error-state"
        >
          {{ errorMessage }}
        </p>
        <div
          v-if="isLoading"
          class="py-10 text-center text-sm text-n-slate-11"
          aria-live="polite"
        >
          {{ t('PROPERTIES.PORTAL_SYNC_OPERATIONS.STATES.LOADING') }}
        </div>
        <div
          v-else-if="!hasSyncs"
          class="py-10 text-center text-sm text-n-slate-11"
          data-test="portal-empty-state"
        >
          {{ t('PROPERTIES.PORTAL_SYNC_OPERATIONS.STATES.EMPTY') }}
        </div>
        <div
          v-else
          class="overflow-x-auto rounded-lg border border-n-weak"
          data-test="portal-sync-table-scroll"
        >
          <table
            class="min-w-[1080px] w-full text-sm"
            data-test="portal-sync-table"
          >
            <thead>
              <tr class="border-b border-n-weak text-left text-n-slate-11">
                <th class="py-2 pl-3">
                  {{ t('PROPERTIES.PORTAL_SYNC_OPERATIONS.TABLE.SYNC') }}
                </th>
                <th>
                  {{ t('PROPERTIES.PORTAL_SYNC_OPERATIONS.TABLE.STATE') }}
                </th>
                <th>
                  {{ t('PROPERTIES.PORTAL_SYNC_OPERATIONS.TABLE.REMOTE') }}
                </th>
                <th>
                  {{ t('PROPERTIES.PORTAL_SYNC_OPERATIONS.TABLE.ATTEMPTS') }}
                </th>
                <th>
                  {{ t('PROPERTIES.PORTAL_SYNC_OPERATIONS.TABLE.READINESS') }}
                </th>
                <th class="pr-3" />
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="sync in syncs"
                :key="sync.id"
                class="border-b border-n-weak align-top"
              >
                <td class="py-3 pl-3">
                  <p class="font-medium text-n-slate-12">
                    {{
                      t('PROPERTIES.PORTAL_SYNC_OPERATIONS.TABLE.SYNC_TITLE', {
                        portal: sync.portal.toUpperCase(),
                        id: sync.id,
                      })
                    }}
                  </p>
                  <p class="max-w-[20rem] break-all text-xs text-n-slate-11">
                    {{
                      t('PROPERTIES.PORTAL_SYNC_OPERATIONS.TABLE.SYNC_META', {
                        listing: sync.imovelListingId || '-',
                        ref: sync.externalRef || '-',
                      })
                    }}
                  </p>
                </td>
                <td class="py-3">
                  <div class="flex flex-col gap-1">
                    <LaisStatusBadge
                      :label="operationalLabel(sync.operationalState)"
                      :tone="portalSyncTone(sync.operationalState)"
                      dot
                    />
                    <p
                      class="max-w-[15rem] break-words text-xs text-n-slate-11"
                    >
                      {{
                        t(
                          'PROPERTIES.PORTAL_SYNC_OPERATIONS.TABLE.DESIRED_OBSERVED',
                          {
                            desired: statusLabel(
                              sync.desiredState || 'unknown'
                            ),
                            observed: statusLabel(
                              sync.observedState || sync.status
                            ),
                          }
                        )
                      }}
                    </p>
                  </div>
                </td>
                <td class="py-3 text-xs text-n-slate-11">
                  <p class="max-w-[16rem] break-all">
                    {{
                      t('PROPERTIES.PORTAL_SYNC_OPERATIONS.TABLE.REMOTE_ID', {
                        id: sync.remoteId || '-',
                      })
                    }}
                  </p>
                  <p class="max-w-[16rem] break-all">
                    {{
                      t(
                        'PROPERTIES.PORTAL_SYNC_OPERATIONS.TABLE.REMOTE_VERSION',
                        {
                          version: sync.remoteVersion || '-',
                        }
                      )
                    }}
                  </p>
                  <p>
                    {{
                      t('PROPERTIES.PORTAL_SYNC_OPERATIONS.TABLE.NEXT_CHECK', {
                        date: formatDateTime(sync.nextStatusCheckAt),
                      })
                    }}
                  </p>
                </td>
                <td class="py-3">
                  <div class="flex flex-wrap gap-1">
                    <span
                      v-for="entry in attemptCountEntries(sync)"
                      :key="`${sync.id}-${entry.status}`"
                      class="max-w-full break-all rounded-md bg-n-alpha-2 px-2 py-1 text-xs text-n-slate-11"
                    >
                      {{
                        t(
                          'PROPERTIES.PORTAL_SYNC_OPERATIONS.TABLE.ATTEMPT_COUNT',
                          {
                            status: entry.status,
                            count: entry.count,
                          }
                        )
                      }}
                    </span>
                    <span
                      v-if="!sync.attemptTotal"
                      class="rounded-md bg-n-alpha-2 px-2 py-1 text-xs text-n-slate-11"
                    >
                      {{
                        t('PROPERTIES.PORTAL_SYNC_OPERATIONS.TABLE.NO_ATTEMPTS')
                      }}
                    </span>
                  </div>
                  <p
                    v-if="sync.operatorReviewRequired"
                    class="mt-1 text-xs text-n-ruby-11"
                  >
                    {{ t('PROPERTIES.PORTAL_SYNC_OPERATIONS.TABLE.REVIEW') }}
                  </p>
                </td>
                <td class="py-3 text-xs text-n-slate-11">
                  <div class="flex flex-col gap-1">
                    <LaisStatusBadge
                      :label="sync.readiness.status"
                      :tone="readinessTone(sync.readiness.status)"
                      size="sm"
                      dot
                    />
                    <p
                      v-if="sync.readiness.divergence.present"
                      class="max-w-[14rem] break-words text-n-ruby-11"
                    >
                      {{
                        t(
                          'PROPERTIES.PORTAL_SYNC_OPERATIONS.READINESS.DIVERGENCE',
                          {
                            desired:
                              sync.readiness.divergence.desiredState || '-',
                            observed:
                              sync.readiness.divergence.observedState || '-',
                          }
                        )
                      }}
                    </p>
                    <p class="max-w-[14rem] break-words">
                      {{ failureLabel(sync.readiness.failure) }}
                    </p>
                  </div>
                </td>
                <td class="py-3 pr-3 text-right">
                  <div class="flex flex-wrap justify-end gap-2">
                    <button
                      type="button"
                      class="whitespace-nowrap rounded-lg border border-n-weak px-2 py-1 text-xs hover:bg-n-alpha-1"
                      :aria-label="
                        t('PROPERTIES.PORTAL_SYNC_OPERATIONS.ACTIONS.DETAILS')
                      "
                      @click="loadSync(sync)"
                    >
                      {{
                        t('PROPERTIES.PORTAL_SYNC_OPERATIONS.ACTIONS.DETAILS')
                      }}
                    </button>
                    <button
                      type="button"
                      class="whitespace-nowrap rounded-lg border border-n-weak px-2 py-1 text-xs hover:bg-n-alpha-1 disabled:opacity-50"
                      :disabled="
                        !canOperatePortals ||
                        !canOperateSync(sync, 'poll') ||
                        isActionLoading('poll', sync.id)
                      "
                      :title="disabledReasonForSync(sync, 'poll')"
                      @click="pollSync(sync)"
                    >
                      {{ t('PROPERTIES.PORTAL_SYNC_OPERATIONS.ACTIONS.POLL') }}
                    </button>
                    <button
                      type="button"
                      class="whitespace-nowrap rounded-lg border border-n-weak px-2 py-1 text-xs hover:bg-n-alpha-1 disabled:opacity-50"
                      :disabled="
                        !canOperatePortals ||
                        !canOperateSync(sync, 'retry') ||
                        isActionLoading('retry', sync.id)
                      "
                      :title="disabledReasonForSync(sync, 'retry')"
                      @click="retrySync(sync)"
                    >
                      {{ t('PROPERTIES.PORTAL_SYNC_OPERATIONS.ACTIONS.RETRY') }}
                    </button>
                    <button
                      type="button"
                      class="whitespace-nowrap rounded-lg border border-n-weak px-2 py-1 text-xs hover:bg-n-alpha-1 disabled:opacity-50"
                      :disabled="
                        !canOperatePortals ||
                        !canOperateSync(sync, 'reconcile') ||
                        isActionLoading('reconcile', sync.id)
                      "
                      :title="disabledReasonForSync(sync, 'reconcile')"
                      @click="reconcileSync(sync)"
                    >
                      {{
                        t('PROPERTIES.PORTAL_SYNC_OPERATIONS.ACTIONS.RECONCILE')
                      }}
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="mt-4 flex items-center justify-between text-sm">
          <button
            class="rounded-lg border border-n-weak px-3 py-1.5 disabled:opacity-50"
            :disabled="!hasPreviousPage"
            @click="changePage(-1)"
          >
            {{ t('PROPERTIES.PORTAL_SYNC_OPERATIONS.PAGINATION.PREVIOUS') }}
          </button>
          <span class="text-n-slate-11">
            {{
              t('PROPERTIES.PORTAL_SYNC_OPERATIONS.PAGINATION.PAGE', {
                page: meta.page || 1,
                total: meta.total_pages || 0,
              })
            }}
          </span>
          <button
            class="rounded-lg border border-n-weak px-3 py-1.5 disabled:opacity-50"
            :disabled="!hasNextPage"
            @click="changePage(1)"
          >
            {{ t('PROPERTIES.PORTAL_SYNC_OPERATIONS.PAGINATION.NEXT') }}
          </button>
        </div>
      </div>

      <aside class="flex min-w-0 flex-col gap-4">
        <LaisCard class="p-4">
          <label class="flex flex-col text-xs text-n-slate-11">
            {{ t('PROPERTIES.PORTAL_SYNC_OPERATIONS.ACTION_REASON') }}
            <textarea
              v-model="operationReason"
              rows="3"
              class="mt-1 rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm"
              :disabled="!canOperatePortals"
            />
          </label>
        </LaisCard>

        <LaisCard class="p-4">
          <div class="flex items-start justify-between gap-3">
            <div>
              <p class="text-sm font-semibold text-n-slate-12">
                {{ t('PROPERTIES.PORTAL_SYNC_OPERATIONS.DETAIL.TITLE') }}
              </p>
              <p class="text-xs text-n-slate-11">
                {{
                  selectedSync
                    ? t('PROPERTIES.PORTAL_SYNC_OPERATIONS.DETAIL.SELECTED', {
                        id: selectedSync.id,
                      })
                    : t('PROPERTIES.PORTAL_SYNC_OPERATIONS.DETAIL.EMPTY')
                }}
              </p>
            </div>
            <span
              v-if="isLoadingSync"
              class="i-lucide-loader-circle size-4 animate-spin text-n-slate-11"
            />
          </div>

          <div v-if="selectedSync" class="mt-4 flex flex-col gap-4">
            <div
              class="grid grid-cols-1 gap-2 text-xs text-n-slate-11 sm:grid-cols-2"
            >
              <div>
                <p>{{ t('PROPERTIES.PORTAL_SYNC_OPERATIONS.TABLE.STATE') }}</p>
                <p class="font-semibold text-n-slate-12">
                  {{ operationalLabel(selectedSync.operationalState) }}
                </p>
              </div>
              <div>
                <p>
                  {{ t('PROPERTIES.PORTAL_SYNC_OPERATIONS.TABLE.READINESS') }}
                </p>
                <p class="font-semibold text-n-slate-12">
                  {{ selectedSync.readiness.status }}
                </p>
              </div>
              <div>
                <p>
                  {{ t('PROPERTIES.PORTAL_SYNC_OPERATIONS.TABLE.ATTEMPTS') }}
                </p>
                <p class="font-semibold text-n-slate-12">
                  {{ selectedSync.attemptTotal }}
                </p>
              </div>
            </div>

            <div
              class="rounded-lg border border-n-weak p-3 text-xs text-n-slate-11"
              data-test="portal-sync-readiness-detail"
            >
              <p class="font-semibold text-n-slate-12">
                {{ failureLabel(selectedSync.readiness.failure) }}
              </p>
              <p v-if="selectedSync.readiness.failure.message" class="mt-1">
                {{ selectedSync.readiness.failure.message }}
              </p>
              <p v-if="selectedSync.readiness.divergence.present" class="mt-1">
                {{
                  t('PROPERTIES.PORTAL_SYNC_OPERATIONS.READINESS.DIVERGENCE', {
                    desired:
                      selectedSync.readiness.divergence.desiredState || '-',
                    observed:
                      selectedSync.readiness.divergence.observedState || '-',
                  })
                }}
              </p>
              <p class="mt-1">
                {{
                  t('PROPERTIES.PORTAL_SYNC_OPERATIONS.READINESS.REDACTION', {
                    provider:
                      selectedSync.readiness.redaction.providerPayloads ||
                      'redacted',
                  })
                }}
              </p>
            </div>

            <div>
              <p class="mb-2 text-xs font-semibold text-n-slate-12">
                {{ t('PROPERTIES.PORTAL_SYNC_OPERATIONS.DETAIL.HISTORY') }}
              </p>
              <div
                v-if="!selectedSync.attemptHistory.length"
                class="rounded-lg border border-n-weak p-3 text-xs text-n-slate-11"
              >
                {{ t('PROPERTIES.PORTAL_SYNC_OPERATIONS.TABLE.NO_ATTEMPTS') }}
              </div>
              <div
                v-for="attempt in selectedSync.attemptHistory"
                :key="attempt.id"
                class="mb-2 rounded-lg border border-n-weak p-3"
              >
                <div class="flex items-start justify-between gap-2">
                  <div class="min-w-0">
                    <p class="break-words text-sm font-medium text-n-slate-12">
                      {{
                        t(
                          'PROPERTIES.PORTAL_SYNC_OPERATIONS.DETAIL.ATTEMPT_TITLE',
                          {
                            action: attempt.action,
                            number: attempt.attemptNumber,
                          }
                        )
                      }}
                    </p>
                    <p class="break-words text-xs text-n-slate-11">
                      {{
                        t(
                          'PROPERTIES.PORTAL_SYNC_OPERATIONS.DETAIL.ATTEMPT_META',
                          {
                            remote: attempt.remoteStatus || '-',
                            date: formatDateTime(attempt.startedAt),
                          }
                        )
                      }}
                    </p>
                    <p
                      v-if="attempt.errorClass"
                      class="mt-1 break-all text-xs text-n-ruby-11"
                    >
                      {{ attempt.errorClass }}
                    </p>
                  </div>
                  <LaisStatusBadge
                    :label="attempt.status"
                    :tone="portalSyncTone(attempt.status)"
                    size="sm"
                    dot
                  />
                </div>
              </div>
            </div>
          </div>
        </LaisCard>
      </aside>
    </section>
  </main>
</template>
